"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Flashbar, {
  type FlashbarProps,
} from "@cloudscape-design/components/flashbar";

import { PACK_DENIED_PARAM } from "./RouteGuard";

/**
 * Renders the one-time toast shown after `<RouteGuard>` redirects a
 * non-pack tenant away from a London-Plan-gated route. Reads the
 * `packDenied=<tenantName>` URL search param, displays the message, and
 * clears the param via `router.replace` so a refresh doesn't re-show it.
 *
 * Designed to mount on the redirect destination (e.g. the summary
 * dashboard). Renders nothing when the param is absent — safe to mount
 * universally.
 *
 * Wording per `epic-15-page-design-system.md` §8.2: "This page is part
 * of the London Plan policy pack and isn't available for [Tenant Name]."
 */
export function PackDeniedToast(): React.JSX.Element | null {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [items, setItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  useEffect(() => {
    const tenantName = searchParams?.get(PACK_DENIED_PARAM);
    if (!tenantName) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- bootstrapping from external state (URL search param); no updates after mount unless dismissed or auto-cleared
    setItems([
      {
        type: "info",
        dismissible: true,
        onDismiss: () => setItems([]),
        content: `This page is part of the London Plan policy pack and isn't available for ${tenantName}.`,
        id: "pack-denied",
      },
    ]);

    // Clear the search param so a refresh doesn't re-show the toast and
    // a copy-pasted URL is clean. router.replace preserves history depth.
    const params = new URLSearchParams(searchParams.toString());
    params.delete(PACK_DENIED_PARAM);
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname);

    // Auto-dismiss after 5 seconds per AC7 + page-layout-sketch-03 ⑧.
    // User can also dismiss earlier via the Flashbar X button. Cleanup
    // handles unmount mid-window.
    const timeout = window.setTimeout(() => setItems([]), 5000);
    return () => window.clearTimeout(timeout);
  }, [searchParams, router, pathname]);

  if (items.length === 0) return null;
  return <Flashbar items={items} />;
}
